/**
 * Matches merchant-managed dropdown templates to the existing header menu.
 * The existing menu remains responsible for focus, hover, ARIA, and height.
 */
class NavigationDropdowns extends HTMLElement {
  /** @type {MutationObserver | null} */
  #headerObserver = null;

  connectedCallback() {
    this.#render();
  }

  disconnectedCallback() {
    this.#headerObserver?.disconnect();
    this.#headerObserver = null;
    this.#clearInjectedCallouts();
  }

  /**
   * Return a stable comparison key for internal and absolute menu URLs.
   * @param {string} value
   * @returns {string}
   */
  #normaliseUrl(value) {
    if (!value) return '';

    try {
      const url = new URL(value, document.baseURI);
      const pathname = url.pathname.replace(/\/+$/, '') || '/';
      return url.origin === window.location.origin ? `${pathname}${url.search}` : url.href;
    } catch {
      return '';
    }
  }

  /**
   * Remove only callouts created by this section instance.
   */
  #clearInjectedCallouts() {
    document
      .querySelectorAll(`[data-navigation-dropdown-source="${CSS.escape(this.id)}"]`)
      .forEach((callouts) => callouts.remove());

    document.querySelectorAll('.mega-menu__grid[data-navigation-dropdown-active]').forEach((grid) => {
      if (!(grid instanceof HTMLElement)) return;
      delete grid.dataset.navigationDropdownActive;
      grid.style.removeProperty('--navigation-dropdown-columns-desktop');
      grid.style.removeProperty('--navigation-dropdown-columns-tablet');
      grid.style.removeProperty('--navigation-dropdown-list-columns-desktop');
      grid.style.removeProperty('--navigation-dropdown-list-columns-tablet');
    });

    document
      .querySelectorAll(`[data-navigation-dropdown-shell="${CSS.escape(this.id)}"]`)
      .forEach((submenu) => submenu.remove());

    document
      .querySelectorAll(`[data-navigation-dropdown-mobile-shell="${CSS.escape(this.id)}"]`)
      .forEach((shell) => {
        if (!(shell instanceof HTMLElement)) return;
        const originalLink = shell.querySelector('template[data-navigation-dropdown-original-link]')?.content
          .firstElementChild;
        if (originalLink instanceof HTMLElement) shell.replaceWith(originalLink);
      });

    document
      .querySelectorAll(`[data-navigation-dropdown-aria-source="${CSS.escape(this.id)}"]`)
      .forEach((link) => {
        link.removeAttribute('aria-controls');
        link.removeAttribute('aria-haspopup');
        link.removeAttribute('aria-expanded');
        delete link.dataset.navigationDropdownAriaSource;
      });
  }

  /**
   * Build the standard Horizon submenu shell for a configured top-level item
   * that does not already own child navigation links.
   * @param {Map<string, HTMLTemplateElement[]>} templates
   */
  #ensureDesktopSubmenuShells(templates) {
    const exampleSubmenu = document.querySelector('.menu-list__submenu');
    const exampleInner = exampleSubmenu?.querySelector('.menu-list__submenu-inner');
    const exampleMegaMenu = exampleSubmenu?.querySelector('.mega-menu');

    document.querySelectorAll('.menu-list__list-item[data-navigation-menu-url]').forEach((item, index) => {
      if (!(item instanceof HTMLElement) || item.querySelector('.mega-menu__grid')) return;
      const key = this.#normaliseUrl(item.dataset.navigationMenuUrl ?? '');
      if (!templates.get(key)?.length) return;

      const link = item.querySelector(':scope > [ref="menuitem"]');
      if (!(link instanceof HTMLElement)) return;

      const submenuId = `navigation-dropdown-${this.id}-${index}`;
      const submenu = document.createElement('div');
      submenu.className = exampleSubmenu?.className || 'menu-list__submenu';
      submenu.setAttribute('ref', 'submenu[]');
      submenu.dataset.navigationDropdownShell = this.id;

      const inner = document.createElement('div');
      inner.id = submenuId;
      inner.className = exampleInner?.className || 'menu-list__submenu-inner';
      if (exampleInner?.getAttribute('style')) inner.setAttribute('style', exampleInner.getAttribute('style') || '');

      const megaMenu = document.createElement('div');
      megaMenu.className =
        exampleMegaMenu?.className || 'mega-menu section section--full-width-margin section--full-width';

      const grid = document.createElement('div');
      grid.className = 'mega-menu__grid';
      grid.dataset.navigationMenuUrl = item.dataset.navigationMenuUrl ?? '';
      grid.style.setProperty('--menu-columns-desktop', '6');
      grid.style.setProperty('--menu-columns-tablet', '4');

      megaMenu.append(grid);
      inner.append(megaMenu);
      submenu.append(inner);
      link.insertAdjacentElement('afterend', submenu);

      link.setAttribute('aria-controls', submenuId);
      link.setAttribute('aria-haspopup', 'true');
      link.setAttribute('aria-expanded', 'false');
      link.dataset.navigationDropdownAriaSource = this.id;
    });
  }

  /**
   * Build the standard Horizon drawer submenu for a configured top-level item
   * that does not already own child navigation links.
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #ensureMobileSubmenuShell(item) {
    const existingInner = item.querySelector('.menu-drawer__inner-submenu');
    if (existingInner instanceof HTMLElement) return existingInner;

    const existingChildList = item.querySelector('.menu-drawer__menu--childlist');
    if (existingChildList) {
      const details = existingChildList.closest('details');
      return details instanceof HTMLElement ? details : item;
    }

    const originalLink = item.querySelector(':scope > a.menu-drawer__menu-item--mainlist');
    if (!(originalLink instanceof HTMLAnchorElement)) return null;

    const itemLabel = originalLink.textContent?.trim() ?? '';
    const backToLabel = (this.dataset.backToLabel ?? 'Back to __title__').replace('__title__', itemLabel);

    const shell = document.createElement('details');
    shell.id = `NavigationDropdownDrawer-${this.id}-${item.dataset.navigationDropdownIndex ?? '0'}`;
    shell.className = 'menu-drawer__menu-container';
    shell.dataset.navigationDropdownMobileShell = this.id;

    const summary = document.createElement('summary');
    summary.id = originalLink.id;
    summary.className = originalLink.className;
    summary.setAttribute('aria-expanded', 'false');
    summary.setAttribute('on:click', 'header-drawer/open/submenu');
    const label = originalLink.querySelector('.menu-drawer__menu-item-text')?.cloneNode(true);
    if (label) summary.append(label);

    const exampleCaret = document.querySelector('.menu-drawer__menu-item--mainlist .icon-caret--forward');
    if (exampleCaret) summary.append(exampleCaret.cloneNode(true));

    const submenu = document.createElement('div');
    submenu.className = 'menu-drawer__submenu has-submenu';
    submenu.tabIndex = -1;

    const inner = document.createElement('div');
    inner.className = 'menu-drawer__inner-submenu';

    const exampleButtons = document.querySelector('.menu-drawer__inner-submenu > .menu-drawer__nav-buttons');
    if (exampleButtons) {
      const buttons = exampleButtons.cloneNode(true);
      if (buttons instanceof HTMLElement) {
        const backLabel = buttons.querySelector('.menu-drawer__back-button .menu-drawer__menu-item-text');
        if (backLabel) backLabel.textContent = itemLabel;
        const backButton = buttons.querySelector('.menu-drawer__back-button');
        if (backButton) backButton.setAttribute('aria-label', backToLabel);
        inner.append(buttons);
      }
    } else {
      const buttons = document.createElement('div');
      buttons.className = 'menu-drawer__nav-buttons';

      const backButton = document.createElement('button');
      backButton.type = 'button';
      backButton.className = 'button menu-drawer__back-button focus-inset';
      backButton.setAttribute('on:click', 'header-drawer/back');
      backButton.setAttribute('aria-label', backToLabel);
      backButton.textContent = itemLabel || this.dataset.backLabel || 'Back';

      const closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'button button-unstyled close-button menu-drawer__close-button focus-inset';
      closeButton.setAttribute('on:click', 'header-drawer/close');
      closeButton.setAttribute('aria-label', this.dataset.closeLabel ?? 'Close');
      closeButton.textContent = this.dataset.closeLabel ?? 'Close';

      buttons.append(backButton, closeButton);
      inner.append(buttons);
    }

    const destinationList = document.createElement('ul');
    destinationList.className = 'menu-drawer__menu menu-drawer__menu--childlist';
    destinationList.setAttribute('role', 'list');
    destinationList.tabIndex = -1;

    const destinationItem = document.createElement('li');
    destinationItem.className = 'menu-drawer__list-item';
    const destinationLink = originalLink.cloneNode(true);
    if (destinationLink instanceof HTMLAnchorElement) {
      destinationLink.removeAttribute('id');
      destinationLink.className = 'menu-drawer__menu-item menu-drawer__menu-item--parent focus-inset';
      const destinationLabel = destinationLink.querySelector('.menu-drawer__menu-item-text');
      if (destinationLabel) destinationLabel.textContent = itemLabel;
      destinationItem.append(destinationLink);
      destinationList.append(destinationItem);
      inner.append(destinationList);
    }

    const originalTemplate = document.createElement('template');
    originalTemplate.dataset.navigationDropdownOriginalLink = '';
    originalTemplate.content.append(originalLink);

    submenu.append(inner);
    shell.append(summary, submenu, originalTemplate);
    item.append(shell);
    return inner;
  }

  /**
   * Clone a configured callout group.
   * @param {HTMLTemplateElement} template
   * @returns {HTMLElement | null}
   */
  #cloneCallouts(template) {
    const fragment = template.content.cloneNode(true);
    if (!(fragment instanceof DocumentFragment)) return null;
    const callouts = fragment.querySelector('[data-navigation-dropdown-callouts]');
    if (!(callouts instanceof HTMLElement)) return null;
    return callouts;
  }

  /**
   * Inject configured cards into their existing menu grid and retain the
   * header's established open/close behaviour.
   */
  #render() {
    this.#headerObserver?.disconnect();
    this.#clearInjectedCallouts();

    const templates = new Map();
    this.querySelectorAll('template[data-navigation-dropdown-template]').forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;
      const key = this.#normaliseUrl(template.dataset.navigationDropdownTemplate ?? '');
      const callouts = this.#cloneCallouts(template);
      if (!key || !callouts || callouts.querySelectorAll('.resource-card--menu-promo').length === 0) return;
      const matches = templates.get(key) ?? [];
      matches.push(template);
      templates.set(key, matches);
    });

    this.#ensureDesktopSubmenuShells(templates);

    const desktopMatchIndexes = new Map();
    document.querySelectorAll('.mega-menu__grid[data-navigation-menu-url]').forEach((grid) => {
      if (!(grid instanceof HTMLElement)) return;
      const key = this.#normaliseUrl(grid.dataset.navigationMenuUrl ?? '');
      const matches = templates.get(key) ?? [];
      const matchIndex = desktopMatchIndexes.get(key) ?? 0;
      const template = matches[matchIndex];
      if (!template) return;
      desktopMatchIndexes.set(key, matchIndex + 1);

      const callouts = this.#cloneCallouts(template);
      if (!callouts) return;

      const cardCount = callouts.querySelectorAll('.resource-card--menu-promo').length;
      if (cardCount === 0) return;

      const desktopColumns = Math.min(cardCount, 3);
      const tabletColumns = Math.min(cardCount, 2);

      callouts.dataset.navigationDropdownSource = this.id;
      grid.dataset.navigationDropdownActive = '';
      grid.style.setProperty('--navigation-dropdown-columns-desktop', `${desktopColumns}`);
      grid.style.setProperty('--navigation-dropdown-columns-tablet', `${tabletColumns}`);
      grid.style.setProperty('--navigation-dropdown-list-columns-desktop', `${6 - desktopColumns}`);
      grid.style.setProperty('--navigation-dropdown-list-columns-tablet', `${4 - tabletColumns}`);
      grid.append(callouts);
    });

    const mobileMatchIndexes = new Map();
    document
      .querySelectorAll(
        '.menu-drawer__navigation > .menu-drawer__menu > .menu-drawer__list-item[data-navigation-menu-url]',
      )
      .forEach((item, itemIndex) => {
        if (!(item instanceof HTMLElement)) return;
        const key = this.#normaliseUrl(item.dataset.navigationMenuUrl ?? '');
        const matches = templates.get(key) ?? [];
        const matchIndex = mobileMatchIndexes.get(key) ?? 0;
        const template = matches[matchIndex];
        if (!template) return;
        mobileMatchIndexes.set(key, matchIndex + 1);

        item.dataset.navigationDropdownIndex = `${itemIndex}`;
        const target = this.#ensureMobileSubmenuShell(item);

        const callouts = this.#cloneCallouts(template);
        if (!callouts || callouts.querySelectorAll('.resource-card--menu-promo').length === 0) return;

        callouts.classList.add('navigation-dropdown__callouts--drawer');
        const wrapper = document.createElement('div');
        wrapper.className =
          'navigation-dropdown__drawer menu-drawer__featured-content menu-drawer__featured-content--childlist';
        wrapper.dataset.navigationDropdownSource = this.id;
        wrapper.append(callouts);

        if (!target) return;
        target.append(wrapper);
      });

    const header = document.querySelector('header-component');
    if (!header) return;

    this.#headerObserver = new MutationObserver(() => this.#render());
    this.#headerObserver.observe(header, { childList: true, subtree: true });
  }
}

if (!customElements.get('navigation-dropdowns')) {
  customElements.define('navigation-dropdowns', NavigationDropdowns);
}
